import { LicensingContext, Localization, SettingsFixedPriorities, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import { Product } from "@viamap/viamap2-common/dist/managers/Types";
import { actionInitializeLicenseManager } from "@viamap/viamap2-common";
import { FormControl } from "react-bootstrap";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput";
import { GlassButton } from "./GlassButtons";
import { useState, useContext } from "react";
import { ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { MapitStateActionType, MapitStateContext } from "src/states/MapitState";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { createPortal } from "react-dom";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { useWindowContext } from "src/WindowManager/useWindowContext";

const settingsPrioritiesToClear: SettingsFixedPriorities[] = [SettingsFixedPriorities.LicenseSettings, SettingsFixedPriorities.CustomerSettings, SettingsFixedPriorities.UserSettings]

export const SimulateOtherUserDialog = (props: { managedWindow: ManagedWindow, onClose: () => void }) => {
   const [userEmail, setUserEmail] = useState<string>("");
   const { dispatch: licencingDispatch } = useContext(LicensingContext);
   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext)

   const { hasAccessToFeature } = useContext(ApplicationStateContext);

   function actionSimulateUser() {
      try {
         let userRef = userEmail;
         let customerRef = ViamapPersistenceLayer.extractCustomerRef(userEmail) || "unknown";
         licencingDispatch(actionInitializeLicenseManager(Product.Mapit, customerRef, userRef));
         mapitStateDispatch({ type: MapitStateActionType.InitializeStandardLayers, payload: { hasAccessToFeatureFunction: hasAccessToFeature } });
         settingsPrioritiesToClear.map((a) => SettingsManager.clearByPriority(a));
         SettingsManager.initializeAfterUserIsKnown({}, customerRef, userRef, Product.Mapit);
         // Dispatch a custom event to notify App of simulation
         window.dispatchEvent(new CustomEvent('simulate-user', { detail: { userEmail } }));
      } catch (err: any) {
         alert(Localization.getText("User simulation failed").replace("{error}", err.message || String(err)))
      }
      props.onClose();
   }

   return (
      <AdvancedDragModal onClose={props.onClose}
         PosDefault={{ left: "5%", top: "95%", transform: "translateY(-100%)" }}
         window={props.managedWindow}
         title={Localization.getText("Simulate other user login")}
      >

         <ADModalBody>
            <GlassInputGroup
               autoFocus={0}
               onSubmit={() => {
                  actionSimulateUser();
               }}
               onEscape={() => {
                  props.onClose();
               }}
            >
               <GlassTextInputTransformer label={Localization.getText("User Email")}>
                  <GlassInputControl>
                     <FormControl type="text" disabled={false} value={userEmail} placeholder={Localization.getText("Email")} onChange={(e) => { setUserEmail(e.target.value) }} />
                  </GlassInputControl>
               </GlassTextInputTransformer>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassButton onClick={() => {
               actionSimulateUser();
            }}>{Localization.getText("Do it")}</GlassButton>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}

export function SimulationBorder() {
   return createPortal(
      <div
         className="mit-localhost-window-frame"
         aria-hidden="true"
         style={{
            // zIndex: 9999,
            boxShadow: "0px 0px 4px 2px #b1005bb7 inset"
         }}
      />,
      document.body
   )

}

export function SimulationPanel(props: { simulatedUserEmail: string | null }) {
   const { dispatch: windowDispatch } = useWindowContext()
   const { dispatch: licencingDispatch } = useContext(LicensingContext);
   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);

   if (!props.simulatedUserEmail) return null;

   function refreshSimulateUser() {
      let userRef = props.simulatedUserEmail;
      try {
         if (!userRef) {
            alert(Localization.getText("No simulated user email provided"));
            return;
         }
         let customerRef = ViamapPersistenceLayer.extractCustomerRef(userRef) || "unknown";
         licencingDispatch(actionInitializeLicenseManager(Product.Mapit, customerRef, userRef));
         mapitStateDispatch({ type: MapitStateActionType.InitializeStandardLayers, payload: { hasAccessToFeatureFunction: hasAccessToFeature } });
         settingsPrioritiesToClear.map((a) => SettingsManager.clearByPriority(a));
         SettingsManager.initializeAfterUserIsKnown({}, customerRef, userRef, Product.Mapit);
         // Dispatch a custom event to notify App of simulation
         window.dispatchEvent(new CustomEvent('simulate-user', { detail: { userEmail: userRef } }));
      } catch (err: any) {
         alert(Localization.getFormattedText("User simulation failed {error}", { error: err.message || String(err) }))
      }
   }

   return (
      <div
         style={{
            zIndex: 9999,
            position: 'fixed',
            left: '50%',
            bottom: 32,
            transform: 'translateX(-50%)',
            background: 'pink',
            color: '#b1005a',
            fontWeight: 'bold',
            fontSize: 20,
            borderRadius: 24,
            boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            minWidth: 320,
            maxWidth: '90vw',
            border: '4px solid #ff69b4',
            gap: 16,
         }}
      >
         <span style={{ marginRight: 16 }}>{Localization.getFormattedText("Simulating user {email}", { email: props.simulatedUserEmail })}</span>
         {
            [
               { i: <MIBIcon icon="refresh" />, title: Localization.getText("Reload simulated user"), fn: () => refreshSimulateUser() },
               { i: <MIBIcon icon="user" />, title: Localization.getText("Switch simulated user"), fn: () => windowDispatch(openWindow(WindowId.SimulateOtherUser)) },
               { i: <MIBIcon icon="infoOutline" />, title: Localization.getText("Show access rights"), fn: () => windowDispatch(openWindow(WindowId.UserAccessRights)) },
               { i: <MIBIcon icon="close" />, title: Localization.getText("Stop user simulation"), fn: () => window.navigation?.reload?.() }
            ].map((k, index) =>
               <button
                  title={k.title}
                  aria-label={k.title}
                  key={index} onClick={() => { k.fn?.() }}
                  style={{
                     marginLeft: 'auto',
                     background: '#fff',
                     color: '#b1005a',
                     border: '2px solid #b1005a',
                     borderRadius: 8,
                     padding: '6px 16px',
                     lineHeight: '1em',
                     fontWeight: 'bold',
                     cursor: 'pointer',
                  }}
               >
                  {k.i}
               </button>
            )
         }
         {createPortal(<SimulationBorder />, document.body)}
      </div>
   );
}



//  const source = props.priority;
//  const settings = removeUndefined(newSettings)
//  props.settingsManager.clearByPriority(source);
//  props.settingsManager.addSettings(settings, { filename: "LiveSetting", priority: source });